import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { apiFetch } from '../services/api';
import './AvisosSino.css';

const INTERVALO_MS = 30000;

function tempoRelativo(valor) {
  const d = new Date(valor);
  if (isNaN(d.getTime())) return '';
  const seg = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seg < 60) return 'agora';
  if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `há ${Math.floor(seg / 3600)} h`;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AvisosSino({ collapsed }) {
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [aberto, setAberto] = useState(false);
  const [painel, setPainel] = useState({ top: 80, left: 300, largura: 360, altura: 480 });
  const [toast, setToast] = useState(null);
  const botaoRef = useRef(null);
  const idsConhecidos = useRef(null); // null = primeira carga (não mostra aviso na tela)
  const toastTimer = useRef(null);

  const carregar = useCallback(async () => {
    try {
      const r = await apiFetch('/notificacoes');
      const lista = Array.isArray(r && r.data) ? r.data : [];
      setAvisos(lista);
      setNaoLidas((r && r.naoLidas) || 0);

      if (idsConhecidos.current === null) {
        idsConhecidos.current = new Set(lista.map((a) => a.id));
        return;
      }
      const novos = lista.filter((a) => !a.lida && !idsConhecidos.current.has(a.id));
      lista.forEach((a) => idsConhecidos.current.add(a.id));
      if (novos.length > 0) {
        setToast(
          novos.length === 1
            ? { titulo: novos[0].titulo, mensagem: novos[0].mensagem, link: novos[0].link }
            : { titulo: `${novos.length} novos avisos`, mensagem: 'Clique para ver os documentos assinados.', link: novos[0].link }
        );
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 10000);
      }
    } catch (e) {
      // silencioso: o aviso é secundário e não deve atrapalhar o sistema
    }
  }, []);

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, INTERVALO_MS);
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
      clearTimeout(toastTimer.current);
    };
  }, [carregar]);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') setAberto(false); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto]);

  const alternar = () => {
    if (!aberto && botaoRef.current) {
      const r = botaoRef.current.getBoundingClientRect();
      const largura = Math.min(360, window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.right + 8, window.innerWidth - largura - 8));
      const top = Math.max(8, Math.min(r.top, window.innerHeight - 220));
      setPainel({ top, left, largura, altura: window.innerHeight - top - 16 });
    }
    setAberto((v) => !v);
  };

  const marcarLida = async (aviso) => {
    if (aviso.lida) return;
    setAvisos((atual) => atual.map((a) => (a.id === aviso.id ? { ...a, lida: true } : a)));
    setNaoLidas((n) => Math.max(0, n - 1));
    try {
      await apiFetch(`/notificacoes/${aviso.id}/lida`, { method: 'POST' });
    } catch (e) {
      carregar();
    }
  };

  const marcarTodas = async () => {
    setAvisos((atual) => atual.map((a) => ({ ...a, lida: true })));
    setNaoLidas(0);
    try {
      await apiFetch('/notificacoes/lidas', { method: 'POST' });
    } catch (e) {
      carregar();
    }
  };

  const abrirAviso = async (aviso) => {
    await marcarLida(aviso);
    setAberto(false);
    if (aviso.link) navigate(aviso.link);
  };

  const abrirToast = () => {
    const destino = toast && toast.link;
    setToast(null);
    setAberto(false);
    carregar();
    if (destino) navigate(destino);
  };

  return (
    <>
      <div
        ref={botaoRef}
        className="nav-item avisos-sino-item"
        role="button"
        tabIndex={0}
        title={collapsed ? 'Avisos' : undefined}
        onClick={alternar}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            alternar();
          }
        }}
      >
        <div className="nav-item-left">
          <span className="avisos-sino-icone">
            <Bell size={18} />
            {naoLidas > 0 && <span className="avisos-sino-badge">{naoLidas > 99 ? '99+' : naoLidas}</span>}
          </span>
          {!collapsed && <span>Avisos</span>}
        </div>
      </div>

      {aberto && createPortal(
        <>
          <div className="avisos-overlay" onClick={() => setAberto(false)} />
          <div
            className="avisos-painel"
            role="dialog"
            aria-label="Avisos"
            style={{ top: painel.top, left: painel.left, width: painel.largura, maxHeight: painel.altura }}
          >
            <div className="avisos-painel-topo">
              <strong>Avisos</strong>
              <div className="avisos-painel-acoes">
                {naoLidas > 0 && (
                  <button type="button" className="avisos-link" onClick={marcarTodas}>
                    <CheckCheck size={14} /> Marcar todos como lidos
                  </button>
                )}
                <button type="button" className="avisos-fechar" onClick={() => setAberto(false)} aria-label="Fechar">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="avisos-lista">
              {avisos.length === 0 ? (
                <p className="avisos-vazio">Nenhum aviso por enquanto.</p>
              ) : (
                avisos.map((a) => (
                  <button
                    type="button"
                    key={a.id}
                    className={`avisos-item ${a.lida ? '' : 'nao-lida'}`}
                    onClick={() => abrirAviso(a)}
                  >
                    <span className="avisos-item-titulo">{a.titulo}</span>
                    <span className="avisos-item-msg">{a.mensagem}</span>
                    <span className="avisos-item-hora">{tempoRelativo(a.created_date)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}

      {toast && createPortal(
        <div className="avisos-toast" role="status" onClick={abrirToast}>
          <button
            type="button"
            className="avisos-toast-fechar"
            aria-label="Fechar aviso"
            onClick={(e) => { e.stopPropagation(); setToast(null); }}
          >
            <X size={14} />
          </button>
          <strong>{toast.titulo}</strong>
          <span>{toast.mensagem}</span>
        </div>,
        document.body
      )}
    </>
  );
}
